import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Box, Chip, Divider, Link, Typography, alpha } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { useTheme } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import DocumentVersionServices from 'services/DocumentVersionServices'
import { includesArrayElements } from 'Utils'
import DashboardContainer from 'components/DashboardContainer'

const ALL_DOCUMENTS_ALLOWED_USER_ROLES = ['Admin', 'National Codex Contact Point', 'National Codex Committee Chair', 'National Codex Committee Secretary', 'National Codex Committee Member']

const normalizeRole = (role = '') => String(role).trim().replace(/\s+/g, ' ').toLowerCase()

const hasAllowedRole = (allowedRoles, userRoles = []) => {
    const normalizedAllowedRoles = allowedRoles.map(normalizeRole)
    const normalizedUserRoles = userRoles.map(normalizeRole)
    return includesArrayElements(normalizedAllowedRoles, normalizedUserRoles)
}

const statusStyles = {
    uploaded: { color: '#2563eb', backgroundColor: '#dbeafe' },
    'Written comments ready to consolidate': { color: '#186f00', backgroundColor: '#eaffc5' },
    consolidated: { color: '#7c3aed', backgroundColor: '#ede9fe' },
    'ncc approved': { color: '#14532d', backgroundColor: '#bbf7d0' },
    approved: { color: '#14532d', backgroundColor: '#bbf7d0' },
    amended: { color: '#e90c00', backgroundColor: '#ffbbbb' },
    'return for revision': { color: '#813c00', backgroundColor: '#fddf8b' },
}

const reviewStyles = {
    'Not Started': { color: '#475569', backgroundColor: '#e2e8f0' },
    'In Progress': { color: '#4338ca', backgroundColor: '#e0e7ff' },
    Completed: { color: '#166534', backgroundColor: '#dcfce7' },
    'Ready for Review': { color: '#9a3412', backgroundColor: '#ffedd5' },
}

const reviewStatusMap = {
    Uploaded: 'In Progress',
    'Written comments ready to consolidate': 'Not Started',
    Consolidated: 'In Progress',
    'NCC Approved': 'Completed',
    Approved: 'Completed',
    Amended: 'Ready for Review',
    'Return for Revision': 'Ready for Review',
}

const formatLabel = (value = '') => {
    const prepared = String(value || '').replaceAll('_', ' ').trim()
    if (!prepared) {
        return 'Uploaded'
    }

    return prepared.replace(/\b\w/g, (char) => char.toUpperCase())
}

const getDeadlineLabel = (value) => {
    if (!value) {
        return 'N/A'
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return 'N/A'
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const getOverdueText = (value) => {
    if (!value) {
        return 'No deadline'
    }

    const date = new Date(value)
    if (Number.isNaN(date.getTime())) {
        return 'No deadline'
    }

    const now = new Date()
    const startOfNow = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const startOfDeadline = new Date(date.getFullYear(), date.getMonth(), date.getDate())
    const diffInDays = Math.floor((startOfNow.getTime() - startOfDeadline.getTime()) / (1000 * 60 * 60 * 24))

    if (diffInDays <= 0) {
        return 'On schedule'
    }

    return `${diffInDays} days overdue`
}

const RecentDocuments = () => {
    const theme = useTheme()
    const navigate = useNavigate()
    const user = useSelector((state) => state.auth.user) || {}
    const [documents, setDocuments] = useState([])
    const [loading, setLoading] = useState(false)

    const rows = useMemo(() => {
        return documents.map((item) => {
            const documentStatus = formatLabel(item?.document_status)
            const reviewStatus = reviewStatusMap[documentStatus] || 'Not Started'
            const deadline = item?.document?.internal_deadline || item?.internal_deadline || item?.document?.codex_deadline || item?.codex_deadline || null

            return {
                id: item?.id,
                number: item?.document?.document_number || '-',
                title: item?.document?.title || item?.file_name || 'Untitled Document',
                documentStatus,
                reviewStatus,
                overdueText: getOverdueText(deadline),
                deadlineLabel: getDeadlineLabel(deadline),
            }
        })
    }, [documents])

    const getData = useCallback(async () => {
        setLoading(true)
        const userId = user?.id
        const isAllDocumentsAllowed = hasAllowedRole(ALL_DOCUMENTS_ALLOWED_USER_ROLES, user?.roles || [])
        const userRoles = user?.roles || []
        const primaryRole = typeof userRoles[0] === 'string' ? userRoles[0] : (userRoles[0]?.name || '')

        try {
            const response = await DocumentVersionServices.getAll({
                page: 0,
                limit: 4,
                ...(isAllDocumentsAllowed ? {} : { user_id: userId }),
                ...(primaryRole ? { role: primaryRole } : {}),
            })

            if (response.success) {
                setDocuments(response?.data?.data || [])
            } else {
                setDocuments([])
                toast.error(response.message || 'Failed to load recent documents.')
            }
        } finally {
            setLoading(false)
        }
    }, [])

    useEffect(() => {
        getData()
    }, [getData])

    return (
        <DashboardContainer
            title="Recent Documents"
            action={
                <Link
                    component="button"
                    underline="none"
                    onClick={() => navigate('/documents')}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.3,
                        fontSize: '0.85rem',
                        fontWeight: 600,
                        color: theme.palette.primary.main,
                    }}
                >
                    View all
                    <ChevronRightIcon sx={{ fontSize: 18 }} />
                </Link>
            }
        >
            {loading && (
                <Typography sx={{ py: 3, color: theme.palette.text.secondary }}>
                    Loading recent documents...
                </Typography>
            )}

            {!loading && rows.length === 0 && (
                <Typography sx={{ py: 3, color: theme.palette.text.secondary }}>
                    No recent documents found.
                </Typography>
            )}

            {!loading && rows.map((item, index) => {
                const normalizedStatus = item.documentStatus.toLowerCase()
                const currentStatusStyle = statusStyles[normalizedStatus] || { color: '#475569', backgroundColor: '#e2e8f0' }
                const currentReviewStyle = reviewStyles[item.reviewStatus] || reviewStyles['Not Started']

                return (
                    <Box key={item.id || `${item.number}-${index}`}>
                        <Box
                            onClick={() => navigate('/documents')}
                            sx={{
                                py: 2,
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: 2,
                                cursor: 'pointer',
                            }}
                        >
                            <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 0.7 }}>
                                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                        {item.number}
                                    </Typography>
                                    <Chip
                                        label={item.documentStatus}
                                        size="small"
                                        sx={{
                                            height: 22,
                                            fontWeight: 500,
                                            fontSize: '0.74rem',
                                            color: currentStatusStyle.color,
                                            backgroundColor: currentStatusStyle.backgroundColor,
                                        }}
                                    />
                                </Box>

                                <Typography
                                    sx={{
                                        fontWeight: 600,
                                        fontSize: { xs: '0.6rem', md: '0.875rem' },
                                        color: theme.palette.text.primary,
                                        mb: 0.9,
                                    }}
                                >
                                    {item.title}
                                </Typography>

                                <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                                    <Chip
                                        label={item.reviewStatus}
                                        size="small"
                                        sx={{
                                            height: 24,
                                            fontWeight: 500,
                                            fontSize: '0.75rem',
                                            color: currentReviewStyle.color,
                                            backgroundColor: currentReviewStyle.backgroundColor,
                                        }}
                                    />
                                    <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                        {item.overdueText}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ textAlign: 'right', minWidth: 60, pt: 0.2 }}>
                                <Typography sx={{ color: theme.palette.error.main, fontWeight: 600, lineHeight: 1.1, fontSize: '0.75rem' }}>
                                    {item.deadlineLabel}
                                </Typography>
                                <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                    11.59 PM
                                </Typography>

                                <Typography sx={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                                    deadline
                                </Typography>
                            </Box>
                        </Box>

                        {index < rows.length - 1 && <Divider />}
                    </Box>
                )
            })}
        </DashboardContainer>
    )
}

export default RecentDocuments